/**
 * RelatorioModal — detalhe da carga com relatório de integridade (RF-M2-006):
 * total, volume, extensões, duplicados, ilegíveis, corrompidos, caminhos longos.
 * ADR-033: arquivos → documentos ("1 PDF → 10 documentos"), falhas de arquivo e de
 * página e páginas além do limite.
 * ADR-040: a seção "Planilhas" — andamento, totais, por planilha e as recusadas com o motivo; o relatório se atualiza
 * sozinho enquanto houver planilha em análise. Só contagens, caminhos e motivos (nunca valores de célula).
 * ADR-043: a seção "Compactados e formatos" — os compactados (extraídos, em extração, recusados com o motivo), os
 * arquivos extraídos (novos e já existentes), os de sistema ignorados e os que não abrem (para pedir o reenvio); o
 * relatório se atualiza sozinho enquanto houver compactado em extração.
 * Requisito 6.3 (emenda de 10/10/2026): "Não abrem (até agora)" — a mesma regra da lista "Arquivos que não abrem", que
 * fica no corpo do relatório e inclui o PDF/TIFF com o motivo exato — no lugar de "Corrompidos" (que contava o `.txt`) e
 * da seção "Arquivos que não puderam ser abertos".
 * ADR-049 (spec carga-robusta): a seção "Caminhos que já estavam no acervo" — o mesmo conteúdo (não processado de novo)
 * e o conteúdo novo (entrou com o nome marcado), com a lista; só quando há.
 */

import { Link } from 'react-router-dom';
import { useCarga } from '../hooks/useCargas';
import { LIMITE_PAGINAS } from '../services/formatos';
import {
  NaoAbremTotais,
  RelatorioIntegridade,
  RelatorioPlanilhas,
  SituacaoPlanilhaDaCarga,
  TipoPlanilhaDaCarga,
} from '../services/cargas-types';
import { formatarBytes, rotuloMotivoCompactado, rotuloMotivoFalha, rotuloMotivoNaoAbre } from './format';

export interface RelatorioModalProps {
  readonly cargaId: string;
  readonly onFechar: () => void;
  /** Intervalo da atualização enquanto houver planilha em análise (ms). */
  readonly intervaloMs?: number;
}

const numero = new Intl.NumberFormat('pt-BR');

/**
 * As contagens de "não abre": as da API; numa API anterior, pela lista que veio; sem a lista (antes da ADR-043), só o
 * total — o `corrompidos` de então contava o que não abre.
 */
function totaisNaoAbrem(r: RelatorioIntegridade): NaoAbremTotais | number {
  if (r.naoAbremTotais) return r.naoAbremTotais;
  if (!r.naoAbrem) return r.corrompidos;
  const lista = r.naoAbrem;
  const conta = (m: string): number => lista.filter((x) => x.motivo === m).length;
  return {
    total: lista.length,
    formatoNaoReconhecido: conta('FORMATO_DESCONHECIDO'),
    vazios: conta('VAZIO'),
    corrompidos: conta('CORROMPIDO'),
    protegidos: conta('PROTEGIDO'),
  };
}

/** "Não abrem (até agora): N — X formato não reconhecido · Y vazio(s) · Z corrompido(s) [· W protegido(s) por senha]". */
function linhaNaoAbrem(t: NaoAbremTotais | number): string {
  const n = (v: number) => numero.format(v);
  if (typeof t === 'number') return `Não abrem (até agora): ${n(t)}`;
  if (t.total === 0) return 'Não abrem (até agora): 0';
  const protegidos = t.protegidos > 0 ? ` · ${n(t.protegidos)} protegido(s) por senha` : '';
  return `Não abrem (até agora): ${n(t.total)} — ${n(t.formatoNaoReconhecido)} formato não reconhecido · ${n(t.vazios)} vazio(s) · ${n(t.corrompidos)} corrompido(s)${protegidos}`;
}

/** A lista dos arquivos que não abrem — no corpo do relatório, para pedir o reenvio (Requisito 6.3). */
function ListaNaoAbrem({ r }: { readonly r: RelatorioIntegridade }): JSX.Element | null {
  const naoAbrem = r.naoAbrem ?? [];
  if (naoAbrem.length === 0) return null;
  return (
    <>
      <h3 style={{ fontSize: 14 }}>Arquivos que não abrem</h3>
      <ul aria-label="Arquivos que não abrem">
        {naoAbrem.map((x) => (
          <li key={x.caminhoOriginal}>
            <span className="mono">{x.caminhoOriginal}</span> — {rotuloMotivoNaoAbre(x.motivo, x.detalhe)}
          </li>
        ))}
      </ul>
      <p className="muted" style={{ fontSize: 12 }}>
        Peça o reenvio dos que não abrem.
      </p>
    </>
  );
}

const ROTULO_SITUACAO_PLANILHA: Readonly<Record<SituacaoPlanilhaDaCarga, string>> = {
  importada: 'Importada',
  importada_com_alertas: 'Importada com alertas',
  recusada: 'Recusada',
  ja_carregada: 'Já carregada',
  em_analise: 'Em análise',
  nao_reconhecida: 'Não reconhecida',
};

/** ADR-046: o tipo da planilha em palavras. */
export const ROTULO_TIPO_PLANILHA: Readonly<Record<TipoPlanilhaDaCarga, string>> = {
  instalacoes: 'Instalações',
  controle: 'Planilha de controle',
  designacao: 'Lista de designação',
  relatorio_fotografico: 'Relatório Fotográfico',
  nao_reconhecida: 'Não reconhecida',
};

type ItemPlanilha = RelatorioPlanilhas['itens'][number];

/** ADR-046: o que foi feito com a planilha, pelo tipo (a de instalações segue com os números de antes). */
export function resumoDaPlanilha(i: ItemPlanilha, n: (v: number) => string): string {
  if (i.situacao === 'recusada' || i.situacao === 'nao_reconhecida') return i.motivo ?? '—';
  const rf = i.relatorioFotografico;
  if (rf) {
    if (rf.emExtracao) return 'em extração (a capa e as fotos)';
    const partes = [
      rf.capaLida ? 'capa lida' : 'capa não lida (o INEP pelo nome do arquivo)',
      `${n(rf.fotos)} foto(s)`,
      `${n(rf.abasSemFoto)} aba(s) sem foto`,
    ];
    if (rf.fotosNaoExtraidas) partes.push('fotos não extraídas');
    if (rf.visitaNova) partes.push('1 visita nova (pela capa)');
    return partes.join(' · ');
  }
  const c = i.controle;
  if (c && i.tipo === 'designacao') return `${n(c.escolas)} escola(s)`;
  if (c) {
    return `${n(c.escolas)} escola(s): ${n(c.visitasNovas)} visita(s) nova(s) · ${n(c.jaTinhamVisita)} já tinham visita (cruzadas) · ${n(c.semSituacao)} sem situação de visita`;
  }
  if (i.situacao === 'em_analise') return '—';
  return `${n(i.linhas)} linha(s) · ${n(i.visitasNovas)} visita(s) nova(s) · ${n(i.referencias)} referência(s) · ${n(i.linhasJaCarregadas)} já carregada(s) · INEP inválido ${n(i.inepInvalido)} · repetido ${n(i.inepRepetido)}`;
}

/** ADR-046: a linha dos totais por tipo (só os tipos que aparecem). */
function linhaPorTipo(t: RelatorioPlanilhas['totais'], n: (v: number) => string): string | null {
  const partes: string[] = [];
  if (t.controle && t.controle.planilhas > 0) {
    partes.push(
      `Planilhas de controle: ${n(t.controle.planilhas)} (${n(t.controle.escolas)} escolas · ${n(t.controle.visitasNovas)} visitas novas · ${n(t.controle.jaTinhamVisita)} já tinham visita · ${n(t.controle.semSituacao)} sem situação)`,
    );
  }
  if (t.designacao && t.designacao.planilhas > 0) {
    partes.push(`Listas de designação: ${n(t.designacao.planilhas)} (${n(t.designacao.escolas)} escolas)`);
  }
  const rf = t.relatoriosFotograficos;
  if (rf && rf.total > 0) {
    partes.push(
      `Relatórios Fotográficos: ${n(rf.total)} (${n(rf.capaLida)} com capa lida · ${n(rf.fotos)} fotos${rf.emExtracao > 0 ? ` · ${n(rf.emExtracao)} em extração` : ''}${rf.fotosNaoExtraidas > 0 ? ` · ${n(rf.fotosNaoExtraidas)} com fotos não extraídas` : ''})`,
    );
  }
  if ((t.naoReconhecidas ?? 0) > 0) partes.push(`Não reconhecidas: ${n(t.naoReconhecidas!)}`);
  return partes.length > 0 ? partes.join(' · ') : null;
}

/** A seção "Planilhas" do relatório da carga (ADR-040). */
function SecaoPlanilhas({ p }: { readonly p: RelatorioPlanilhas }): JSX.Element {
  const t = p.totais;
  const a = t.alertasPorMotivo;
  const n = (v: number) => numero.format(v);
  return (
    <section aria-label="Planilhas" style={{ marginTop: 16 }}>
      <h3 style={{ fontSize: 14 }}>Planilhas</h3>
      <p role="status">
        {`Planilhas: ${n(p.andamento.analisadas)} de ${n(p.andamento.planilhas)} analisadas`}
        {p.andamento.itensPendentes > 0 ? ` · ${n(p.andamento.itensPendentes)} linha(s) ainda em conferência` : ''}
      </p>
      <ul>
        <li>{`${n(t.importadas)} importada(s) · ${n(t.comAlertas)} com alertas · ${n(t.recusadas)} recusada(s) · ${n(t.jaCarregadas)} já carregada(s) · ${n(t.emAnalise)} em análise`}</li>
        <li>{`Visitas novas: ${n(t.visitasNovas)} · referências (a escola tem termo com imagem): ${n(t.referencias)} · linhas já carregadas: ${n(t.linhasJaCarregadas)}`}</li>
        <li>{`INEP inválido: ${n(t.inepInvalido)} (${n(t.inepCorrigido)} corrigidos pelo nome no município · ${n(t.inepNaoCorrigido)} marcados) · INEP repetido: ${n(t.inepRepetido)}`}</li>
        {t.planilhasComColunaAusente > 0 && <li>{`Planilhas com coluna ausente: ${n(t.planilhasComColunaAusente)}`}</li>}
        {linhaPorTipo(t, n) && <li>{linhaPorTipo(t, n)}</li>}
      </ul>
      <p className="muted" style={{ fontSize: 12 }}>
        {`Alertas — ICCID perdido: ${n(a.iccid_perdido)} · "EM TRABALHO": ${n(a.em_trabalho)} · coordenada não recuperada: ${n(a.coordenada_nao_recuperada)} · Coluna ausente na planilha: ${n(a.coluna_ausente)} · INEP repetido: ${n(a.inep_repetido)} · INEP inválido não corrigido: ${n(a.inep_invalido)}`}
      </p>
      <Link to="/visitas?evidencia=planilha&marcador=ALERTA">Ver as visitas da planilha com alerta</Link>
      <table className="table" style={{ marginTop: 8 }}>
        <thead>
          <tr>
            <th>Planilha</th>
            <th>Tipo</th>
            <th>Situação</th>
            <th>Aba</th>
            <th>Números</th>
          </tr>
        </thead>
        <tbody>
          {p.itens.map((i) => (
            <tr key={i.caminho}>
              <td className="mono">{i.caminho}</td>
              <td>{i.tipo ? ROTULO_TIPO_PLANILHA[i.tipo] : '—'}</td>
              <td>{ROTULO_SITUACAO_PLANILHA[i.situacao] ?? i.situacao}</td>
              <td>{i.aba ?? '—'}</td>
              <td>
                {resumoDaPlanilha(i, n)}
                {i.colunasAusentes.length > 0 && <div className="muted">{`Colunas ausentes: ${i.colunasAusentes.join(', ')}`}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {p.recusadas.length > 0 && (
        <>
          <h4 style={{ fontSize: 13 }}>Recusadas</h4>
          <ul aria-label="Planilhas recusadas">
            {p.recusadas.map((r) => (
              <li key={r.caminho}>
                <span className="mono">{r.caminho}</span> — {r.motivo}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/** A seção "Compactados e formatos" do relatório da carga (ADR-043) — só quando há o que mostrar. */
function SecaoCompactados({ r }: { readonly r: RelatorioIntegridade }): JSX.Element | null {
  const c = r.compactados;
  const e = r.arquivosExtraidos;
  const sistema = r.sistema ?? 0;
  if ((c?.total ?? 0) === 0 && (e?.total ?? 0) === 0 && sistema === 0) {
    return null;
  }
  const n = (v: number) => numero.format(v);
  return (
    <section aria-label="Compactados e formatos" style={{ marginTop: 16 }}>
      <h3 style={{ fontSize: 14 }}>Compactados e formatos</h3>
      <ul>
        {c && c.total > 0 && (
          <li>{`Compactados: ${n(c.total)} — ${n(c.extraidos)} extraído(s) · ${n(c.emExtracao)} em extração · ${n(c.recusados.length)} recusado(s)`}</li>
        )}
        {e && e.total > 0 && (
          <li>{`Arquivos extraídos: ${n(e.total)} — ${n(e.novos)} novo(s) · ${n(e.jaExistentes)} já existente(s) no acervo`}</li>
        )}
        {c && c.entradasRecusadas > 0 && (
          <li>{`${n(c.entradasRecusadas)} arquivo(s) de dentro dos compactados recusado(s) (caminho inválido, link, senha, corrompido ou acima de 5 GB)`}</li>
        )}
        {sistema > 0 && <li>{`Arquivos de sistema ignorados: ${n(sistema)}`}</li>}
      </ul>
      {c && c.recusados.length > 0 && (
        <>
          <h4 style={{ fontSize: 13 }}>Compactados recusados</h4>
          <ul aria-label="Compactados recusados">
            {c.recusados.map((x) => (
              <li key={x.caminhoOriginal}>
                <span className="mono">{x.caminhoOriginal}</span> — {rotuloMotivoCompactado(x.motivo)}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/** A seção "Caminhos que já estavam no acervo" do relatório da carga (ADR-049) — só quando há repetido. */
function SecaoRepetidos({ r }: { readonly r: RelatorioIntegridade }): JSX.Element | null {
  const rep = r.repetidos;
  const total = rep ? rep.mesmoConteudo + rep.conteudoNovo : 0;
  if (!rep || total === 0) {
    return null;
  }
  const n = (v: number) => numero.format(v);
  return (
    <section aria-label="Caminhos que já estavam no acervo" style={{ marginTop: 16 }}>
      <h3 style={{ fontSize: 14 }}>Caminhos que já estavam no acervo</h3>
      <ul>
        <li>{`Já estavam no acervo (mesmo conteúdo): ${n(rep.mesmoConteudo)} — não foram processados de novo`}</li>
        <li>{`Mesmo caminho, conteúdo novo: ${n(rep.conteudoNovo)} — entraram com o nome marcado`}</li>
      </ul>
      <ul aria-label="Caminhos repetidos">
        {rep.lista.map((x) => (
          <li key={x.caminho}>
            <span className="mono">{x.caminho}</span>
            {x.caminhoRegistrado ? (
              <>
                {' → entrou como '}
                <span className="mono">{x.caminhoRegistrado}</span>
              </>
            ) : (
              ' — já estava no acervo'
            )}
          </li>
        ))}
      </ul>
      {rep.lista.length < total && (
        <p className="muted" style={{ fontSize: 12 }}>
          {`Mostrando ${n(rep.lista.length)} de ${n(total)} caminhos.`}
        </p>
      )}
    </section>
  );
}

export function RelatorioModal({ cargaId, onFechar, intervaloMs }: RelatorioModalProps): JSX.Element {
  const { detalhe, carregando, erro } = useCarga(cargaId, intervaloMs);

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Relatório da carga">
      <div className="modal">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>Relatório de integridade</h2>
          <button type="button" className="btn btn-ghost" onClick={onFechar} aria-label="Fechar">
            Fechar
          </button>
        </div>
        {carregando && <p className="muted">Carregando…</p>}
        {erro && <p className="notice">Não foi possível carregar o relatório.</p>}
        {detalhe && detalhe.relatorio && (
          <div>
            <p className="muted mono">{detalhe.cargaId}</p>
            <ul>
              <li>Total de arquivos: {detalhe.relatorio.total}</li>
              <li>Volume: {formatarBytes(detalhe.relatorio.volumeBytes)}</li>
              <li>Duplicados: {detalhe.relatorio.duplicados}</li>
              <li>Ilegíveis (até agora): {detalhe.relatorio.ilegiveis}</li>
              <li>{linhaNaoAbrem(totaisNaoAbrem(detalhe.relatorio))}</li>
              <li>Caminhos longos: {detalhe.relatorio.caminhosLongos.length}</li>
              {typeof detalhe.relatorio.documentos === 'number' && (
                <li>
                  Documentos (até agora): {detalhe.relatorio.documentos} — {detalhe.relatorio.total} arquivo(s) →{' '}
                  {detalhe.relatorio.documentos} documento(s); cada página de um PDF é um documento
                </li>
              )}
            </ul>
            <p className="muted" style={{ fontSize: 12 }}>
              Ilegíveis e os que não abrem crescem depois da conclusão da carga, conforme o pré-processamento e a
              extração avançam.
            </p>
            <h3 style={{ fontSize: 14 }}>Extensões</h3>
            <ul>
              {Object.entries(detalhe.relatorio.extensoes).map(([ext, qtd]) => (
                <li key={ext}>
                  {ext}: {qtd}
                </li>
              ))}
            </ul>
            {(detalhe.relatorio.paginasAlemDoLimite ?? 0) > 0 && (
              <p className="notice notice-info">
                {detalhe.relatorio.paginasAlemDoLimite} página(s) além do limite de {LIMITE_PAGINAS} por arquivo não
                foram processadas.
              </p>
            )}
            {/* Requisito 6.3: o PDF/TIFF que não abriu está na lista "Arquivos que não abrem", com o motivo exato. */}
            <ListaNaoAbrem r={detalhe.relatorio} />
            {(detalhe.relatorio.falhasPagina?.length ?? 0) > 0 && (
              <>
                <h3 style={{ fontSize: 14 }}>Páginas com falha</h3>
                <ul>
                  {detalhe.relatorio.falhasPagina?.map((f) => (
                    <li key={`${f.caminhoOriginal}#${f.pagina}`}>
                      <span className="mono">{f.caminhoOriginal}</span> — página {f.pagina}: {rotuloMotivoFalha(f.motivo)}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <SecaoCompactados r={detalhe.relatorio} />
            <SecaoRepetidos r={detalhe.relatorio} />
            {detalhe.relatorio.planilhas && <SecaoPlanilhas p={detalhe.relatorio.planilhas} />}
            {detalhe.relatorio.divergencias && detalhe.relatorio.divergencias.length > 0 && (
              <div className="notice" style={{ marginTop: 12 }}>
                Divergências detectadas: {detalhe.relatorio.divergencias.map((d) => d.motivo).join('; ')}
              </div>
            )}
          </div>
        )}
        {detalhe && !detalhe.relatorio && (
          <p className="notice notice-info">
            Relatório ainda não disponível — a carga está em andamento.
          </p>
        )}
      </div>
    </div>
  );
}
